import React, { useState } from "react";

const UnsettledPayments = ({
  unsettledPayments = [],
  employeeData,
  onApprove,
  onApproveAll,
}) => {
  const [openDate, setOpenDate] = useState(null);
  const [settleRequest, setSettleRequest] = useState(null); // { type: "single" | "all", ids, date }
  const [settlementMethod, setSettlementMethod] = useState("CASH");

  const toggleDate = (date) => {
    setOpenDate((prev) => (prev === date ? null : date));
  };

  const openSettleModal = (type, ids, date) => {
    setSettlementMethod("CASH");
    setSettleRequest({ type, ids, date });
  };

  const closeSettleModal = () => setSettleRequest(null);

  const confirmSettle = () => {
    if (!settleRequest) return;
    const { type, ids, date } = settleRequest;
    if (type === "all") {
      onApproveAll && onApproveAll(ids, date, settlementMethod);
    } else {
      onApprove && onApprove(ids[0], date, settlementMethod);
    }
    setSettleRequest(null);
  };

  // Empty state
  if (!unsettledPayments || unsettledPayments.length === 0) {
    return (
      <div className="w-full flex items-center justify-center py-16 bg-gray-50">
        <div className="max-w-md text-center bg-white rounded-2xl shadow border border-gray-200 px-6 py-8">
          <p className="text-sm text-gray-500 mb-2">Payments</p>
          <p className="text-lg font-semibold text-gray-900">
            No payment items available for{" "}
            <span className="text-[#7F58E6]">
              {employeeData?.fullName || "this employee"}
            </span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-50">
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-200 bg-gray-50">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Unsettled Payments
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {employeeData?.fullName || "Employee"}
            </p>
          </div>
          <div className="mt-3 sm:mt-0 text-sm text-gray-500 flex flex-col items-start sm:items-end">
            <span className="font-medium">
              {unsettledPayments.length} day
              {unsettledPayments.length > 1 ? "s" : ""} of payments
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6">
          <div className="space-y-4">
            {unsettledPayments.map((group) => (
              <div
                key={group.date}
                className="border border-gray-200 rounded-xl overflow-hidden bg-white"
              >
                {/* Date row (accordion header) */}
                <button
                  type="button"
                  onClick={() => toggleDate(group.date)}
                  className="w-full flex items-center justify-between px-4 py-3 sm:px-5 sm:py-3.5 bg-white hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-400 text-white text-sm font-semibold">
                      {group.payments.length}
                    </span>
                    <div className="flex flex-col items-start">
                      <span className="text-sm font-medium text-gray-900">
                        {group.date}
                      </span>
                      <span className="text-xs text-gray-500">
                        {group.payments.length} payment
                        {group.payments.length > 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {onApproveAll && group.payments.some(inv => inv.settleStatus === null || inv.settleStatus === "UNSETTLED" || inv.settleStatus === "REQUESTED") && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const unsettledIds = group.payments
                            .filter(inv => inv.settleStatus === null || inv.settleStatus === "UNSETTLED" || inv.settleStatus === "REQUESTED")
                            .map(inv => inv.id);
                          if (unsettledIds.length > 0) {
                            openSettleModal("all", unsettledIds, group.date);
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs font-bold transition-colors shadow-sm border border-emerald-200"
                      >
                        Settle All
                      </button>
                    )}
                    <span className="text-gray-500 text-lg">
                      {openDate === group.date ? "−" : "+"}
                    </span>
                  </div>
                </button>

                {/* Payments list */}
                {openDate === group.date && (
                  <div className="bg-gray-50 border-t border-gray-200">
                    <ul className="divide-y divide-gray-200">
                      {group.payments.map((inv, idx) => (
                        <li
                          key={idx}
                          className="px-4 py-3 sm:px-5 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-900">
                              ₹{inv.settlementAmount ?? inv.price}
                            </p>
                            {inv.paymentType === "Split" && (
                              <p className="text-xs text-gray-400">
                                Booking total ₹{inv.price}
                              </p>
                            )}
                            <p className="text-xs text-gray-500">
                              Slot: {inv.timeSlot || "--"}
                            </p>
                            <p className="text-xs text-gray-500">
                              Payment Mode: {inv.paymentType || "Unknown"}
                            </p>
                          </div>

                          <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm">
                            {inv.settleStatus === "UNSETTLED" && (
                              <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-300 cursor-not-allowed text-xs font-medium">
                                Unsettled
                              </span>
                            )}

                            {(inv.settleStatus === null || inv.settleStatus === "UNSETTLED") && (
                              <button
                                type="button"
                                onClick={() =>
                                  openSettleModal("single", [inv.id], group.date)
                                }
                                className="px-3 py-1 rounded-full bg-[#22c55e] hover:bg-[#16a34a] text-white text-xs font-semibold"
                              >
                                Settle now
                              </button>
                            )}

                            {inv.settleStatus === "REQUESTED" && (
                              <button
                                type="button"
                                onClick={() =>
                                  openSettleModal("single", [inv.id], group.date)
                                }
                                className="px-3 py-1 rounded-full bg-[#22c55e] hover:bg-[#16a34a] text-white text-xs font-semibold"
                              >
                                Approve
                              </button>
                            )}

                            {inv.settleStatus === "SETTLED" && (
                              <span className="px-3 py-1 rounded-full bg-emerald-900/40 border border-emerald-500/60 text-emerald-300 text-xs font-medium">
                                Settled
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Settlement method modal */}
      {settleRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
            <p className="text-sm font-semibold text-gray-900">
              Settle {settleRequest.ids.length} payment
              {settleRequest.ids.length > 1 ? "s" : ""}
            </p>
            <p className="text-xs text-gray-500 mt-1 mb-4">
              Choose how this amount was collected before settling.
            </p>

            <label className="block text-xs font-medium text-gray-600 mb-1">
              Settlement Method
            </label>
            <select
              value={settlementMethod}
              onChange={(e) => setSettlementMethod(e.target.value)}
              className="w-full mb-5 px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#7F58E6]"
            >
              <option value="CASH">Cash</option>
              <option value="ONLINE">Online</option>
            </select>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeSettleModal}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 border border-gray-300 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSettle}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#22c55e] hover:bg-[#16a34a]"
              >
                Confirm Settle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnsettledPayments;
